import React, { useEffect, useState, Children } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import { LanguageProvider } from './i18n';
import { HomePage } from './pages/HomePage';
import { ServicesPage } from './pages/ServicesPage';
import { ServiceDetailPage } from './pages/ServiceDetailPage';
import { AboutPage } from './pages/AboutPage';
import { ContactPage } from './pages/ContactPage';
import { PricesPage } from './pages/PricesPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { CookiePolicyPage } from './pages/CookiePolicyPage';
import { PatientPrivacyNoticePage } from './pages/PatientPrivacyNoticePage';
import { TermsAndConditionsPage } from './pages/TermsAndConditionsPage';
import { BusinessCardPage } from './pages/BusinessCardPage';
import { BusinessCardNewPage } from './pages/BusinessCardNewPage';
import { BusinessCardNewUaPage } from './pages/BusinessCardNewUaPage';
import { BusinessCardNewRuPage } from './pages/BusinessCardNewRuPage';
import { UkrainianDentistPage } from './pages/UkrainianDentistPage';
import { ImplantyLondonPage } from './pages/ImplantyLondonPage';
import { VeneersLondonPage } from './pages/VeneersLondonPage';
import { RootCanalLondonPage } from './pages/RootCanalLondonPage';
import { EmergencyDentistLondonPage } from './pages/EmergencyDentistLondonPage';
import { RussianDentistPage } from './pages/RussianDentistPage';
import { ImplantyLondonRuPage } from './pages/ImplantyLondonRuPage';
import { VeneersLondonRuPage } from './pages/VeneersLondonRuPage';
import { RootCanalLondonRuPage } from './pages/RootCanalLondonRuPage';
import { EmergencyDentistLondonRuPage } from './pages/EmergencyDentistLondonRuPage';
import { BlogIndexPage } from './pages/blog/BlogIndexPage';
import { ImplantyCostArticle } from './pages/blog/ImplantyCostArticle';
import { NhsVsPrivateArticle } from './pages/blog/NhsVsPrivateArticle';
import { WithoutInsuranceArticle } from './pages/blog/WithoutInsuranceArticle';
import { ToothPainArticle } from './pages/blog/ToothPainArticle';
import { VeneersGuideArticle } from './pages/blog/VeneersGuideArticle';
import { ChooseDentistArticle } from './pages/blog/ChooseDentistArticle';
import { SitemapPage } from './pages/SitemapPage';
import { OrthodonticsPage } from './pages/OrthodonticsPage';
import { ClearAlignersPage } from './pages/ClearAlignersPage';
import { InvisalignAlignersPage } from './pages/InvisalignAlignersPage';
import { MetalBracesPage } from './pages/MetalBracesPage';
import { CeramicBracesPage } from './pages/CeramicBracesPage';
import { RetainersPage } from './pages/RetainersPage';
import { ChildrensOrthodonticsPage } from './pages/ChildrensOrthodonticsPage';
import { OrthodonticAppliancesPage } from './pages/OrthodonticAppliancesPage';
import { HygienePage } from './pages/HygienePage';
import { GiftCertificate } from './components/GiftCertificate';
import { PrintPricePage } from './pages/PrintPricePage';
import { InfluencerInvitePage } from './pages/InfluencerInvitePage';
import { RaffleVoucherPage } from './pages/RaffleVoucherPage';
import { LogoPage } from './pages/LogoPage';
import { OrthodonticFlyerUaPage } from './pages/OrthodonticFlyerUaPage';
import { PaediatricDentistryPage } from './pages/PaediatricDentistryPage';
import { WhatsAppQrPage } from './pages/WhatsAppQrPage';
import { PatientExperiencePage } from './pages/PatientExperiencePage';
import { ResultsPage } from './pages/ResultsPage';
import { ReviewsPage } from './pages/ReviewsPage';
import { AiSupportPage } from './pages/AiSupportPage';
// ── Global scroll-to-top on every route change ──
// Exception: /prices#anchor — let PricesPage handle its own hash scrolling
function ScrollToTop() {
  const {
    pathname,
    hash
  } = useLocation();
  useEffect(() => {
    // Disable browser scroll restoration globally
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);
  useEffect(() => {
    // If navigating to /prices with a hash, skip — PricesPage handles it
    const isPricesWithHash = pathname.includes('/prices') && hash;
    if (isPricesWithHash) return;
    // Immediate scroll
    window.scrollTo(0, 0);
    // Reliable delayed scroll for dynamic content / mobile
    requestAnimationFrame(() => {
      window.scrollTo(0, 0);
    });
    const t = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 50);
    return () => clearTimeout(t);
  }, [pathname, hash]);
  return null;
}
// Detect user's preferred language from browser
function detectUserLanguage(): 'en' | 'ua' | 'ru' | 'ro' {
  const browserLangs = navigator.languages || [navigator.language];
  for (const lang of browserLangs) {
    const langCode = lang.toLowerCase().split('-')[0];
    if (langCode === 'uk' || langCode === 'ua') {
      return 'ua';
    }
    if (langCode === 'ru') {
      return 'ru';
    }
    if (langCode === 'ro') {
      return 'ro';
    }
    if (langCode === 'en') {
      return 'en';
    }
  }
  return 'en';
}
function getStoredLanguage(): 'en' | 'ua' | 'ru' | 'ro' | null {
  try {
    const stored = localStorage.getItem('dentico-preferred-lang');
    if (stored === 'en' || stored === 'ua' || stored === 'ru' || stored === 'ro') {
      return stored as 'en' | 'ua' | 'ru' | 'ro';
    }
  } catch {
    // localStorage not available
  }
  return null;
}
function setStoredLanguage(lang: 'en' | 'ua' | 'ru' | 'ro') {
  try {
    localStorage.setItem('dentico-preferred-lang', lang);
  } catch {
    // localStorage not available
  }
}
function LanguageRedirect() {
  const navigate = useNavigate();
  useEffect(() => {
    const storedLang = getStoredLanguage();
    if (storedLang) {
      navigate(`/${storedLang}`, {
        replace: true
      });
      return;
    }
    const detectedLang = detectUserLanguage();
    setStoredLanguage(detectedLang);
    navigate(`/${detectedLang}`, {
      replace: true
    });
  }, [navigate]);
  return null;
}
function LanguageRoutes() {
  const {
    lang
  } = useParams();
  const validLangs = ['en', 'ua', 'ru', 'ro'];
  if (!validLangs.includes(lang || '')) {
    return <Navigate to="/en" replace data-id="element-0" data-mp="1wt96dem7hvrf" />;
  }
  useEffect(() => {
    if (lang && validLangs.includes(lang)) {
      setStoredLanguage(lang as 'en' | 'ua' | 'ru' | 'ro');
    }
  }, [lang]);
  return <LanguageProvider data-id="element-1" data-mp="25snwgcygo4be">
      <Routes data-id="element-2" data-mp="25s4aml5owvs3">
        <Route index element={<HomePage data-id="element-4" data-mp="25va38adj2fsk" />} data-id="element-3" data-mp="25rm8x909dpcs" />
        <Route path="ukrainskyi-stomatolog-london" element={<UkrainianDentistPage data-id="element-6" data-mp="2kobvzosu438y" />} data-id="element-5" data-mp="2kq6ih7jr83f5" />
        <Route path="implanty-london" element={<ImplantyLondonPage data-id="element-8" data-mp="2fqtugjh5cqaz" />} data-id="element-7" data-mp="2frv54mu93as6" />
        <Route path="veneers-london" element={<VeneersLondonPage data-id="element-10" data-mp="2thowpcx5495l" />} data-id="element-9" data-mp="2tiqk8gvceo47" />
        <Route path="root-canal-london" element={<RootCanalLondonPage data-id="element-12" data-mp="2phet26wtlutw" />} data-id="element-11" data-mp="2thymp0i6qgba" />
        <Route path="emergency-dentist-london" element={<EmergencyDentistLondonPage data-id="element-14" data-mp="33go5cvhwo4sl" />} data-id="element-13" data-mp="2pig3c960idak" />
        <Route path="russkij-stomatolog-london" element={<RussianDentistPage data-id="element-16" data-mp="2yimg1stvz98o" />} data-id="element-15" data-mp="33h7epnthpkv0" />
        <Route path="implantaciya-london" element={<ImplantyLondonRuPage data-id="element-18" data-mp="3i33g1d2umguv" />} data-id="element-17" data-mp="2yfqfrxukg8h2" />
        <Route path="viniry-london" element={<VeneersLondonRuPage data-id="element-20" data-mp="3i1s76yujqiww" />} data-id="element-19" data-mp="3i464lecp877m" />
        <Route path="lechenie-kanalov-london" element={<RootCanalLondonRuPage data-id="element-22" data-mp="385gx8fifi55m" />} data-id="element-21" data-mp="3n2zktejdecbh" />
        <Route path="neotlozhnaya-stomatologiya-london" element={<EmergencyDentistLondonRuPage data-id="element-24" data-mp="3d40i0cladawe" />} data-id="element-23" data-mp="384fb9fzvjy94" />
        <Route path="blog" element={<BlogIndexPage data-id="element-26" data-mp="2zd57ywz1wnr8" />} data-id="element-25" data-mp="3d2p77stfb2vs" />
        <Route path="blog/dental-implants-cost-london" element={<ImplantyCostArticle data-id="element-28" data-mp="343ll0fyym7d2" />} data-id="element-27" data-mp="2zcvdaw329sf7" />
        <Route path="blog/nhs-vs-private-dentistry-uk" element={<NhsVsPrivateArticle data-id="element-30" data-mp="2pfqrixd4nyyx" />} data-id="element-29" data-mp="344n7rhpc8iam" />
        <Route path="blog/dental-treatment-uk-without-insurance" element={<WithoutInsuranceArticle data-id="element-32" data-mp="2uek50psezpz3" />} data-id="element-31" data-mp="2pgse9z3ia9wh" />
        <Route path="blog/toothache-london-what-to-do" element={<ToothPainArticle data-id="element-34" data-mp="2ggdpgprc2co8" />} data-id="element-33" data-mp="2gej5bdnvyol7" />
        <Route path="blog/veneers-london-everything-you-need-to-know" element={<VeneersGuideArticle data-id="element-36" data-mp="30v3vmqe1h010" />} data-id="element-35" data-mp="2kge3waefmnva" />
        <Route path="blog/how-to-choose-dentist-angel" element={<ChooseDentistArticle data-id="element-38" data-mp="2vw0v9j907hzd" />} data-id="element-37" data-mp="30uvfmpmmdell" />
        <Route path="sitemap" element={<SitemapPage data-id="element-40" data-mp="2qwxtc7oblrvm" />} data-id="element-39" data-mp="2vu7pe667t1sf" />
        <Route path="services" element={<ServicesPage data-id="element-42" data-mp="2qvcyegy3g6x8" />} data-id="element-41" data-mp="2qw689qscncjl" />
        <Route path="services/hygiene" element={<HygienePage data-id="element-44" data-mp="2lx32230mv0a1" />} data-id="element-43" data-mp="2ly4qd96ntj9p" />
        <Route path="services/paediatric-dentistry" element={<PaediatricDentistryPage data-id="element-46" data-mp="2hxkra02j05kx" />} data-id="element-45" data-mp="2lxux9cqbivzs" />
        <Route path="services/orthodontics" element={<OrthodonticsPage data-id="element-48" data-mp="2d5j5nnjgx2i8" />} data-id="element-47" data-mp="2hycdwle5asz2" />
        {/* Orthodontic Appliances — one localized slug per language */}
        <Route path="services/orthodontic-appliances" element={<OrthodonticAppliancesPage data-id="element-50" data-mp="237v9qua4loyh" />} data-id="element-49" data-mp="287ri15puw2ju" />
        <Route path="services/ortodontychni-aparaty" element={<OrthodonticAppliancesPage data-id="element-52" data-mp="1y7h23dcfsh0z" />} data-id="element-51" data-mp="2396d4sznsup2" />
        <Route path="services/ortodonticheskie-apparaty" element={<OrthodonticAppliancesPage data-id="element-54" data-mp="1u7qh5q9lcv4d" />} data-id="element-53" data-mp="1yad1z77uhfs2" />
        <Route path="services/aparate-ortodontice" element={<OrthodonticAppliancesPage data-id="element-56" data-mp="9r8u4j1326wf" />} data-id="element-55" data-mp="9pfmb0eqhgmu" />
        <Route path="services/orthodontics/clear-aligners" element={<ClearAlignersPage data-id="element-58" data-mp="ip2q1suxqyj2" />} data-id="element-57" data-mp="dpgctjf3xia9" />
        <Route path="services/orthodontics/invisalign-style-aligners" element={<InvisalignAlignersPage data-id="element-60" data-mp="nncn6908097b" />} data-id="element-59" data-mp="iq3zjswazcyo" />
        <Route path="services/orthodontics/metal-braces" element={<MetalBracesPage data-id="element-62" data-mp="3m484mqzi28r4" />} data-id="element-61" data-mp="3m6b5k626xmam" />
        <Route path="services/orthodontics/ceramic-braces" element={<CeramicBracesPage data-id="element-64" data-mp="3w4h5ii4fsicu" />} data-id="element-63" data-mp="3r4usa8olz241" />
        <Route path="services/orthodontics/retainers" element={<RetainersPage data-id="element-66" data-mp="4pjk96pljz9x" />} data-id="element-65" data-mp="3w3fsw97rvnt0" />
        <Route path="services/orthodontics/children" element={<ChildrensOrthodonticsPage data-id="element-68" data-mp="33gbh9le6cb89" />} data-id="element-67" data-mp="4ql4o1si5y4b" />
        <Route path="services/:serviceId" element={<ServiceDetailPage data-id="element-70" data-mp="38ed1i9nc6glb" />} data-id="element-69" data-mp="33fjpymrm0zns" />
        <Route path="prices" element={<PricesPage data-id="element-72" data-mp="38ctkggtuw1ih" />} data-id="element-71" data-mp="38dlfnqjjjx88" />
        <Route path="price-list" element={<PrintPricePage data-id="element-74" data-mp="2f63mvygwdugl" />} data-id="element-73" data-mp="2f7dkynnr32n8" />
        <Route path="ai-support" element={<AiSupportPage data-id="element-76" data-mp="2k5ya9s1gs1ww" />} data-id="element-75" data-mp="2f6vdsvzjv40j" />
        <Route path="about" element={<AboutPage data-id="element-78" data-mp="2k4dgi4mz4n03" />} data-id="element-77" data-mp="2k56rjhsytyo1" />
        <Route path="patient-experience" element={<PatientExperiencePage data-id="element-80" data-mp="2p51e595pk23a" />} data-id="element-79" data-mp="2k81cdafw5lhz" />
        <Route path="results" element={<ResultsPage data-id="element-82" data-mp="2u4e6g9fcm1d9" />} data-id="element-81" data-mp="2p62qri2dgwn4" />
        <Route path="reviews" element={<ReviewsPage data-id="element-84" data-mp="2u3w02jyz26rm" />} data-id="element-83" data-mp="2u4nvnuskk4hw" />
        <Route path="contact" element={<ContactPage data-id="element-86" data-mp="1wh5lcfj9eubs" />} data-id="element-85" data-mp="2u21mxrtxziy3" />
        <Route path="privacy-policy" element={<PrivacyPolicyPage data-id="element-88" data-mp="1wesv7f3cldnn" />} data-id="element-87" data-mp="1whykaubz8fjd" />
        <Route path="cookie-policy" element={<CookiePolicyPage data-id="element-90" data-mp="21g8gn89ftm6k" />} data-id="element-89" data-mp="21f76r745r5qf" />
        <Route path="patient-privacy-notice" element={<PatientPrivacyNoticePage data-id="element-92" data-mp="26d8p1ptrf10u" />} data-id="element-91" data-mp="21ddv1deotvbk" />
        <Route path="terms-and-conditions" element={<TermsAndConditionsPage data-id="element-94" data-mp="2b6ut4tluxwl1" />} data-id="element-93" data-mp="2b59wmyfzg3kj" />
        <Route path="*" element={<Navigate to={`/${lang}`} replace data-id="element-96" data-mp="1dp35h6efzi2l" />} data-id="element-95" data-mp="1dqnwwn5gmkw8" />
      </Routes>
    </LanguageProvider>;
}
export function App() {
  return <BrowserRouter data-id="element-97" data-mp="1inedrcp39kip">
      <ScrollToTop data-id="element-98" data-mp="1imw9bss9vzzp" />
      <Routes data-id="element-99" data-mp="3vz8po537tceh">
        <Route path="/business-card" element={<BusinessCardPage data-id="element-101" data-mp="3vypinjf3s8f8" />} data-id="element-100" data-mp="3vzsapuo5wgwq" />
        <Route path="/business-card-new" element={<BusinessCardNewPage data-id="element-103" data-mp="3ryymf06t503b" />} data-id="element-102" data-mp="3rxx88muhvxhd" />
        <Route path="/business-card-new-ua" element={<BusinessCardNewUaPage data-id="element-105" data-mp="3mxr9yntpzcqb" />} data-id="element-104" data-mp="3rvsuzeaphm4f" />
        <Route path="/business-card-new-ru" element={<BusinessCardNewRuPage data-id="element-107" data-mp="3hyy0r7lgu7vy" />} data-id="element-106" data-mp="3n0ncyqkfcrlm" />
        <Route path="/gift-certificate" element={<GiftCertificate data-id="element-109" data-mp="3d1oa5omcbq6j" />} data-id="element-108" data-mp="3d0mpcsfivpbm" />
        <Route path="/influencer-invite" element={<InfluencerInvitePage data-id="element-111" data-mp="389eedryjnvwc" />} data-id="element-110" data-mp="3d1xuowom954u" />
        <Route path="/raffle-voucher" element={<RaffleVoucherPage data-id="element-113" data-mp="3883a7r16sm4p" />} data-id="element-112" data-mp="3894os5hevqr6" />
        <Route path="/whatsapp-qr" element={<WhatsAppQrPage data-id="element-115" data-mp="348ufojybok83" />} data-id="element-114" data-mp="347jemrw9hqko" />
        <Route path="/logo" element={<LogoPage data-id="element-117" data-mp="2z9rljujvryew" />} data-id="element-116" data-mp="345yhqvmh5vjn" />
        <Route path="/orthodontic-flyer-ua" element={<OrthodonticFlyerUaPage data-id="element-119" data-mp="2u9vibxj0cb0e" />} data-id="element-118" data-mp="2zaj7serl8jsi" />
        <Route path="/" element={<LanguageRedirect data-id="element-121" data-mp="2uc9fcbx8zxal" />} data-id="element-120" data-mp="2u9n5u6qsr7pq" />
        <Route path="/:lang/*" element={<LanguageRoutes data-id="element-123" data-mp="2pcd7u2pcdnqa" />} data-id="element-122" data-mp="2pajz8hv64tfn" />
        <Route path="*" element={<LanguageRedirect data-id="element-125" data-mp="2fng455yuvgtd" />} data-id="element-124" data-mp="2pd52nbb47hfi" />
      </Routes>
    </BrowserRouter>;
}